import { useState, useEffect, useRef, createContext, useContext, ReactNode } from "react";
import { QueryClientContext } from "@tanstack/react-query";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import type { AppRole } from "@/types/auth";
import { deleteLegacyPrivateCaches } from "@/lib/privateCache";

interface Profile {
  id: string;
  user_id: string;
  full_name: string;
  avatar_url: string | null;
  phone: string | null;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  role: AppRole | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  hasRole: (role: AppRole) => boolean;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [loading, setLoading] = useState(true);

  const queryClient = useContext(QueryClientContext);
  // Session boundary: identity whose data is currently allowed in state/cache, plus a
  // generation that invalidates every in-flight profile/role read of a previous identity.
  const identityRef = useRef<string | null>(null);
  const generationRef = useRef(0);
  const mountedRef = useRef(true);

  const fetchUserData = async (userId: string, generation: number) => {
    const current = () => mountedRef.current && generationRef.current === generation;
    let nextProfile: Profile | null = null;
    let nextRole: AppRole | null = null;
    try {
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (!profileError && profileData) nextProfile = profileData as Profile;

      const { data: roleData, error: roleError } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .maybeSingle();
      if (!roleError && roleData) nextRole = roleData.role as AppRole;
    } catch (error) {
      console.error("Error fetching user data:", error);
    }
    // A stale read (other identity, logout or unmount) never repopulates state.
    if (!current()) return;
    setProfile(nextProfile);
    setRole(nextRole);
    setLoading(false);
  };

  /** Applies a session; crosses the boundary only when the effective user id changes. */
  const applySession = (nextSession: Session | null) => {
    if (!mountedRef.current) return;
    const nextId = nextSession?.user?.id ?? null;
    const prevId = identityRef.current;
    setSession(nextSession);
    setUser(nextSession?.user ?? null);
    if (nextId === prevId) {
      // Token refresh / same user: keep data. Same-null (INITIAL_SESSION/SIGNED_OUT while
      // signed out) settles loading so a discarded initial snapshot never leaves a splash.
      if (nextId === null) setLoading(false);
      return;
    }

    identityRef.current = nextId;
    const generation = ++generationRef.current;
    // Never expose the previous identity's profile/role while the next one loads.
    setProfile(null);
    setRole(null);
    if (prevId !== null && queryClient) {
      void queryClient.cancelQueries();
      queryClient.clear();
    }
    if (prevId !== null) void deleteLegacyPrivateCaches();

    if (nextId) {
      setLoading(true);
      setTimeout(() => { void fetchUserData(nextId, generation); }, 0);
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    mountedRef.current = true;
    // Per-effect flag (StrictMode remount safe) and auth-event counter: the initial
    // getSession() snapshot is discarded if any auth event arrived after it was requested.
    let active = true;
    let authEvents = 0;

    // Set up auth state listener BEFORE checking session. Every event goes through the
    // boundary; same-user events (TOKEN_REFRESHED, USER_UPDATED, INITIAL_SESSION) keep data.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      authEvents++;
      applySession(session);
    });

    const requestedAt = authEvents;
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (!active || authEvents !== requestedAt) return; // stale snapshot: a newer event already applied
        applySession(session ?? null);
      })
      .catch((error) => {
        console.error("Error reading session:", error);
        if (!active || authEvents !== requestedAt) return;
        applySession(null); // fail closed, settles loading
      });

    return () => {
      active = false;
      mountedRef.current = false;
      generationRef.current++;
      // A remount (StrictMode) must hydrate again instead of trusting a voided read.
      identityRef.current = null;
      subscription.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: "https://tkesteticaavancada.com.br",
          data: {
            full_name: fullName,
          },
        },
      });
      return { error: error as Error | null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return { error: error as Error | null };
    } catch (error) {
      return { error: error as Error };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } finally {
      applySession(null);
    }
  };

  const hasRole = (checkRole: AppRole) => {
    return role === checkRole;
  };

  const isAdmin = role === "admin";

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        role,
        loading,
        signUp,
        signIn,
        signOut,
        hasRole,
        isAdmin,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
